import { eulerConfigs } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { ENABLED_EULER_V2_CHAINS } from '../../../utils'
import { fetchEulerPositionsFromApi } from './eulerDataApi'

/**
 * All 256 EVC sub-account indexes (0-255).
 * Kept for backward compatibility but no longer used as default.
 */
export const ALL_EULER_SUB_ACCOUNT_INDEXES: number[] = Array.from(
  { length: 256 },
  (_, i) => i,
)

// ---------------------------------------------------------------------------
// Sub-account discovery via the Euler Data API (V3)
//
// Replaces the deprecated Goldsky `euler-v2-<chain>` subgraphs. Discovery hits
// `/accounts/{owner}/positions?forceFresh=true` (live on-chain lens read), so
// freshly-opened sub-accounts are visible immediately. Memoized briefly so the
// concurrent build + parse calls of one request share a single fetch.
// ---------------------------------------------------------------------------

const ACCOUNT_CACHE_TTL_MS = 5_000

// Caches the API-derived active index set, or `undefined` when the API is
// unsupported on the chain or the request failed (callers then fall back).
const accountCache = new Map<
  string,
  {
    promise: Promise<number[] | undefined>
    timer: ReturnType<typeof setTimeout>
  }
>()

/** Distinct sub-account indexes from the API positions, owner-relative. */
function indexesFromApiPositions(
  positions: { account: string }[],
  owner: string,
): number[] {
  const set = new Set<number>()
  for (const p of positions) {
    if (p.account) set.add(getSubAccountIndex(p.account, owner))
  }
  if (set.size === 0) set.add(0)
  return [...set]
}

/**
 * Memoized API discovery. Resolves to the active sub-account index set, or
 * `undefined` when the chain is unsupported / the request failed.
 */
function fetchEulerActiveIndexesViaApi(
  chainId: string,
  owner: string,
): Promise<number[] | undefined> {
  const key = `${chainId}:${owner.toLowerCase()}`
  const cached = accountCache.get(key)
  if (cached) return cached.promise

  const promise = fetchEulerPositionsFromApi(chainId, owner)
    .then((positions) =>
      positions === undefined
        ? undefined
        : indexesFromApiPositions(positions, owner),
    )
    .catch(() => undefined)

  const timer = setTimeout(() => accountCache.delete(key), ACCOUNT_CACHE_TTL_MS)
  accountCache.set(key, { promise, timer })
  return promise
}

/**
 * Active sub-account indexes for an owner (true set; for the next-account
 * route). Falls back to [0] when the API is unsupported or fails.
 */
export async function fetchEulerSubAccountIndexes(
  chainId: string,
  owner: string,
): Promise<number[]> {
  return (await fetchEulerActiveIndexesViaApi(chainId, owner)) ?? [0]
}

/**
 * Sub-account indexes to actually query in the user-data build. Uses the exact
 * API-discovered set when available; otherwise (API-unsupported chain or a
 * failed request) scans all 256 indexes on-chain via the lens multicall — the
 * parser drops empty sub-accounts — so no chain regresses and no Goldsky is
 * involved.
 */
async function resolveEulerBuildIndexes(
  chainId: string,
  owner: string,
): Promise<number[]> {
  return (
    (await fetchEulerActiveIndexesViaApi(chainId, owner)) ??
    ALL_EULER_SUB_ACCOUNT_INDEXES
  )
}

// ---------------------------------------------------------------------------
// Resolved-indexes store — written by build, read by parse (sync)
// ---------------------------------------------------------------------------

const RESOLVED_STORE_TTL_MS = 60_000

const resolvedIndexesStore = new Map<
  string,
  { indexes: number[]; timer: ReturnType<typeof setTimeout> }
>()

/**
 * Synchronous read of previously resolved sub-account indexes.
 * Returns `undefined` when no prior build has populated the store.
 */
export function getResolvedEulerSubAccountIndexes(
  chainId: string,
  account: string,
): number[] | undefined {
  return resolvedIndexesStore.get(`${chainId}:${account.toLowerCase()}`)
    ?.indexes
}

function storeResolvedIndexes(
  chainId: string,
  account: string,
  indexes: number[],
): void {
  const key = `${chainId}:${account.toLowerCase()}`
  const existing = resolvedIndexesStore.get(key)
  if (existing) clearTimeout(existing.timer)
  const timer = setTimeout(
    () => resolvedIndexesStore.delete(key),
    RESOLVED_STORE_TTL_MS,
  )
  resolvedIndexesStore.set(key, { indexes, timer })
}

// ---------------------------------------------------------------------------
// Sub-account helpers
// ---------------------------------------------------------------------------

/**
 * Derives a sub-account address from an owner address and index (0-255).
 * Uses the EVC formula: `address(uint160(owner) ^ uint160(accountId))`.
 * Since accountId is uint8, only the last byte is affected via XOR.
 */
function getSubAccountAddress(owner: string, index: number): string {
  const prefix = owner.slice(0, 40) // "0x" + 38 hex chars (19 bytes)
  const ownerLastByte = parseInt(owner.slice(40), 16)
  const newLastByte = (ownerLastByte ^ index) & 0xff
  const suffix = newLastByte.toString(16).padStart(2, '0')
  return (prefix + suffix).toLowerCase()
}

/**
 * Extracts the sub-account index from a sub-account address given its owner.
 * Reverses the EVC formula: `index = lastByte(subAccountAddr) ^ lastByte(owner)`.
 */
function getSubAccountIndex(addr: string, owner?: string): number {
  const addrLastByte = parseInt(addr.slice(40), 16)
  if (!owner) return addrLastByte
  const ownerLastByte = parseInt(owner.slice(40), 16)
  return (addrLastByte ^ ownerLastByte) & 0xff
}

/**
 * Resolve the sorted, deduplicated list of sub-account indexes to query.
 *
 * Always includes index 0 (the owner's own address, since owner ^ 0 = owner)
 * in addition to the requested set.
 */
export function resolveSubAccountIndexes(
  _account: string,
  subAccountIndexes: number[],
): number[] {
  const indices = new Set<number>(subAccountIndexes)
  indices.add(0) // Sub-account 0 IS the owner (owner ^ 0 = owner)
  return [...indices].sort((a, b) => a - b)
}

// ---------------------------------------------------------------------------
// Build multicall calls
// ---------------------------------------------------------------------------

/**
 * Build multicall calls for fetching Euler V2 user data.
 *
 * For each sub-account index in the resolved set, creates one
 * `getAccountEnabledVaultsInfo(evc, subAccountAddr)` call
 * via the AccountLens contract.
 *
 * When no `subAccountIndexes` are provided, discovers active sub-accounts via
 * the Euler Data API; on unsupported chains / API failure it scans all 256
 * indexes on-chain (empties are dropped by the parser).
 *
 * @param subAccountIndexes - Numeric sub-account indexes to fetch.
 *   When undefined, resolved via {@link resolveEulerBuildIndexes}.
 *   The owner's native index is always included automatically.
 *   Pass a narrower set (e.g. [0]) for simulations where only one account is needed.
 */
export const buildEulerUserCall = async (
  chainId: string,
  lender: string,
  account: string,
  subAccountIndexes?: number[],
): Promise<Call[]> => {
  if (!ENABLED_EULER_V2_CHAINS.includes(chainId)) return []
  const config = eulerConfigs()?.[lender]?.[chainId]
  if (!config) return []

  const accountLensAddress = config.accountLens
  const evcAddress = config.evc
  if (!accountLensAddress || !evcAddress) return []

  // Resolve indexes: explicit > Euler API discovery > on-chain 256 scan
  const indexes =
    subAccountIndexes ?? (await resolveEulerBuildIndexes(chainId, account))

  const sortedIndices = resolveSubAccountIndexes(account, indexes)

  // Store for the sync parse side to read later
  storeResolvedIndexes(chainId, account, indexes)

  return sortedIndices.map((i) => ({
    address: accountLensAddress,
    name: 'getAccountEnabledVaultsInfo',
    params: [evcAddress, getSubAccountAddress(account, i)],
  }))
}

export { getSubAccountAddress, getSubAccountIndex }
